import type { Theme } from '@mui/material/styles'
import { alpha } from '@mui/material/styles'
import { OrderStatus, OrderStatusLabel, parseOrderStatus as _parseOrderStatus } from '@/types/enums/OrderStatus'

export { OrderStatus, OrderStatusLabel } from '@/types/enums/OrderStatus'
export function parseOrderStatus(value: unknown): OrderStatus {
  return _parseOrderStatus(value)
}

/** Single source of truth for order status → theme color. Used by My Orders list and Order Details. */
export function getOrderStatusColor(
  status: OrderStatus,
  theme: Pick<Theme, 'palette'>
): string {
  switch (status) {
    case OrderStatus.Pending:
      return theme.palette.info.main
    case OrderStatus.Confirmed:
      return theme.palette.primary.main
    case OrderStatus.Preparing:
      return theme.palette.secondary.main
    case OrderStatus.Ready:
      return theme.palette.text.disabled
    case OrderStatus.Packaging:
    case OrderStatus.ReadyToShip:
      return theme.palette.secondary.main
    case OrderStatus.Shipped:
    case OrderStatus.OutForDelivery:
      return theme.palette.warning.main
    case OrderStatus.Delivered:
    case OrderStatus.Completed:
      return theme.palette.success.main
    case OrderStatus.Cancelled:
    case OrderStatus.Returned:
    case OrderStatus.Refunded:
      return theme.palette.error.main
    default:
      return theme.palette.text.disabled
  }
}

/** Display label for status. Use OrderStatusLabel[status] — this helper accepts raw API value. */
export function getOrderStatusLabel(status: OrderStatus): string {
  return OrderStatusLabel[status]
}

/** Microcopy for each status. Uses same wording as labels. */
export const OrderStatusSubtext: Record<OrderStatus, string> = {
  [OrderStatus.Unknown]: 'Tracking your order.',
  [OrderStatus.Pending]: "We've received your order.",
  [OrderStatus.Confirmed]: 'The restaurant has accepted your order.',
  [OrderStatus.Preparing]: 'Your food is being prepared.',
  [OrderStatus.Ready]: 'Your order is ready for pickup.',
  [OrderStatus.Packaging]: 'Your order is being packed.',
  [OrderStatus.ReadyToShip]: 'Ready for dispatch.',
  [OrderStatus.Shipped]: 'Your order is on the way.',
  [OrderStatus.OutForDelivery]: "Out for delivery — it's almost there!",
  [OrderStatus.Delivered]: 'Delivered. Enjoy your meal!',
  [OrderStatus.Cancelled]: 'This order was cancelled.',
  [OrderStatus.Returned]: 'This order was returned.',
  [OrderStatus.Refunded]: 'Your order has been refunded.',
  [OrderStatus.Completed]: 'Order completed. Thank you!',
}

export function getOrderStatusSubtext(status: OrderStatus): string {
  return OrderStatusSubtext[status] ?? 'Tracking your order.'
}

/** Shared MUI sx for the status pill. Use for both list and details. */
export const STATUS_PILL_SX = {
  display: 'inline-block',
  py: 0.75,
  px: 2,
  borderRadius: '9999px',
  fontWeight: 700,
  fontSize: '0.9rem',
} as const

/** Shadow level for order cards. Same intensity on My Orders and Order Details. */
const ORDER_CARD_SHADOW = 1

/** Shared MUI sx for status card: left border + shadow. Use for both list and details. */
export function getStatusCardSx(statusColor: string, theme: Pick<Theme, 'shadows'>) {
  return {
    borderLeft: `4px solid ${statusColor}`,
    boxShadow: theme.shadows[ORDER_CARD_SHADOW],
  }
}

/** Shared MUI sx for order section cards (no status border). Same shadow as status cards. */
export function getOrderSectionCardSx(theme: Pick<Theme, 'shadows'>) {
  return { boxShadow: theme.shadows[ORDER_CARD_SHADOW] }
}

/**
 * Warning/info callout surface used on Order Details recovery cards
 * (orange border + tinted background). Prefer this over ad-hoc divider/hover cards.
 */
export function getOrderWarningCalloutSx(theme: Theme) {
  return {
    ...getOrderSectionCardSx(theme),
    border: `1px solid ${alpha(theme.palette.warning.main, 0.28)}`,
    borderLeft: `3px solid ${alpha(theme.palette.warning.main, 0.55)}`,
    backgroundColor: alpha(
      theme.palette.warning.main,
      theme.palette.mode === 'light' ? 0.06 : 0.14
    ),
  }
}

/** Internal padding: horizontal (px). Use for card content. */
export const ORDER_CARD_PX = 1.25

/** Section header padding (p). */
export const ORDER_CARD_HEADER_P = 1.25

/** Section content: vertical (py) and horizontal (px) — compact mobile density. */
export const ORDER_CARD_CONTENT_PY = 1
export const ORDER_CARD_CONTENT_PX = ORDER_CARD_PX

/** Gap between stacked section cards on Order Details. */
export const ORDER_CARD_GAP = 1

/** Default flex gap inside dense order section bodies. */
export const ORDER_CARD_INNER_GAP = 0.75

/** Horizontal padding for the main content area on My Orders and Order Details. Same on both for consistent header/content alignment. */
export const ORDER_PAGE_CONTENT_PX = 1.5

/** sessionStorage key for saving My Orders scroll position when navigating to Order Details. Restored on return. */
export const MY_ORDERS_SCROLL_KEY = 'getmyfood_my_orders_scroll'

// ─── Typography (order number, section headers, body, muted, totals) ─────────────────────────

/** Order number e.g. #123, #ORD-123. Use color: theme.palette.text.primary */
export const ORDER_TYPO_ORDER_NUMBER = {
  fontSize: '1rem',
  fontWeight: 700,
  lineHeight: 1.25,
} as const

/** Section headers: "Billing Details", "Order Details", "X items in this order". Use color: theme.palette.text.primary */
export const ORDER_TYPO_SECTION_HEADER = {
  fontSize: '1rem',
  fontWeight: 600,
  lineHeight: 1.25,
} as const

/** Body text and row values. Use color: theme.palette.text.primary or .secondary as needed */
export const ORDER_TYPO_BODY = {
  fontSize: '0.875rem',
  fontWeight: 500,
  lineHeight: 1.4,
} as const

/** Muted labels e.g. "Order ID", "Item Total". Use color: theme.palette.text.secondary */
export const ORDER_TYPO_MUTED_LABEL = {
  fontSize: '0.75rem',
  fontWeight: 400,
} as const

/** Muted micro: status subtext, "Payment pending", "• 2 items". Use color: theme.palette.text.disabled, lineHeight 1.3 */
export const ORDER_TYPO_MUTED_MICRO = {
  fontSize: '0.6875rem',
  fontWeight: 400,
  lineHeight: 1.3,
} as const

/** Line/item amounts. Same font weight as totals; smaller size. Use color: theme.palette.text.primary */
export const ORDER_TYPO_AMOUNT = {
  fontSize: '0.875rem',
  fontWeight: 600,
  lineHeight: 1.4,
} as const

/** Totals and important numbers e.g. Grand Total, list amount. Use color: theme.palette.text.primary (total) or .secondary (list amount) */
export const ORDER_TYPO_TOTAL = {
  fontSize: '1rem',
  fontWeight: 600,
  lineHeight: 1.25,
} as const

/** Payment status chip: size and color. Use with MUI Chip. Paid: color="success"; Pending/Not Paid: color="warning". */
export const PAYMENT_CHIP_SIZE = 'small' as const
